import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Car, Globe, Mic, TrendingUp, Users, Zap, CheckCircle2, Star } from 'lucide-react';

const STATS = [
  { value: '1M+', label: 'George\'s Followers' },
  { value: '1.2B', label: 'Total Views' },
  { value: '<500ms', label: 'Voice Latency' },
  { value: '24/7', label: 'Always On' },
];

function AnimatedCounter({ target, suffix = '' }) {
  return (
    <span>
      {target}{suffix}
    </span>
  );
}


export default function GatewayPage() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null); // 'auto' | 'voice' | null
  const [selected, setSelected] = useState(null);

  const handleSelect = (path) => {
    setSelected(path);
    setTimeout(() => navigate(path), 600);
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: '#080808', color: '#fafafa' }}
    >
      {/* Background noise texture */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.06) 0%, transparent 60%),
                          radial-gradient(ellipse at 80% 20%, rgba(201,168,76,0.04) 0%, transparent 50%)`,
      }} />

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 h-16">
        <div className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          <span className="text-white">Saliba</span>
          <span style={{ color: '#C9A84C' }}>Labs</span>
          <span className="text-white/40">.ai</span>
        </div>

        {/* Nav links */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'For Dealerships', href: '/auto' },
            { label: 'Voice Platform', href: '/voice' },
            { label: 'Admin', href: '/admin' },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              className="text-sm text-white/60 hover:text-white transition-colors relative group"
            >
              {label}
              <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-0 group-hover:w-full h-px transition-all duration-200" style={{ background: '#C9A84C' }} />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <img
            src="https://ugc.production.linktr.ee/c1557bd3-0db3-4f04-a35b-b6332f19a89e_IMG-0412.jpeg"
            alt="George Saliba"
            className="w-8 h-8 rounded-full object-cover border border-[#C9A84C]/30"
          />
          <span className="text-sm text-white/60 hidden sm:block">@georgejsaliba</span>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-12">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full border border-[#C9A84C]/25 bg-[#C9A84C]/8"
        >
          <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
          <span className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase">
            George Saliba&rsquo;s Latest Investment — Now Open
          </span>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-4"
        >
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5"
            style={{ fontFamily: 'Space Grotesk' }}
          >
            The smartest move
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #F1E3B0 0%, #C9A84C 50%, #8F6E1C 100%)' }}
            >
              your business can make.
            </span>
          </h1>
          <p className="text-white/55 text-lg max-w-xl mx-auto leading-relaxed">
            SalibaLabs turns missed opportunities into closed deals —
            whether you run a dealership or any other business.
            <br />
            <span className="text-white/75 font-medium">Where does your business fit?</span>
          </p>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-6 sm:gap-10 mb-12 flex-wrap justify-center"
        >
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-xl sm:text-2xl font-bold" style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}>
                {s.value}
              </div>
              <div className="text-xs text-white/45 mt-0.5">{s.label}</div>
            </div>
          ))}
        </motion.div>

        {/* CHOICE CARDS */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid sm:grid-cols-2 gap-5 w-full max-w-3xl"
        >
          {/* AUTOMOTIVE CARD */}
          <motion.button
            onClick={() => handleSelect('/auto')}
            onHoverStart={() => setHovered('auto')}
            onHoverEnd={() => setHovered(null)}
            whileHover={{ y: -4, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="relative text-left rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer group"
            style={{
              borderColor: hovered === 'auto' ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.02)',
              boxShadow: hovered === 'auto' ? '0 0 40px rgba(201,168,76,0.12), 0 24px 60px rgba(0,0,0,0.6)' : '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            {/* Card bg image */}
            <div className="absolute inset-0 opacity-20 transition-opacity duration-300 group-hover:opacity-30"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1681856447108-4cc76fff51c2?crop=entropy&cs=srgb&fm=jpg&q=85)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,8,8,0.92) 0%, rgba(8,8,8,0.75) 100%)' }} />

            <div className="relative z-10 p-7">
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}>
                  <Car size={24} />
                </div>
                <motion.div
                  animate={{ x: hovered === 'auto' ? 0 : -4, opacity: hovered === 'auto' ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight size={20} style={{ color: '#C9A84C' }} />
                </motion.div>
              </div>

              <div className="font-mono-accent text-[10px] text-[#C9A84C] tracking-widest uppercase mb-2">For Dealerships</div>
              <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
                I run a dealership.
              </h2>
              <p className="text-sm text-white/60 leading-relaxed mb-5">
                Backed by George Saliba — the technology that turns your CRM into a
                revenue engine. Every missed call, cold lead, and untouched contact becomes an opportunity.
              </p>

              <div className="space-y-2">
                {['Inbound + outbound calls handled 24/7', 'DriveCentric & vAuto integrated', 'Reactivate cold CRM leads automatically'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 size={12} style={{ color: '#C9A84C', flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
              </div>

              <div
                className="mt-6 flex items-center justify-between px-4 py-3 rounded-xl"
                style={{
                  background: hovered === 'auto' ? '#C9A84C' : 'rgba(201,168,76,0.1)',
                  transition: 'background 0.2s',
                }}
              >
                <span className="text-sm font-semibold" style={{ color: hovered === 'auto' ? '#0a0a0a' : '#C9A84C' }}>
                  {selected === '/auto' ? 'Taking you there…' : 'Join the Waitlist'}
                </span>
                <ArrowRight size={16} style={{ color: hovered === 'auto' ? '#0a0a0a' : '#C9A84C' }} />
              </div>
            </div>
          </motion.button>

          {/* VOICE / BUSINESS CARD */}
          <motion.button
            onClick={() => handleSelect('/voice')}
            onHoverStart={() => setHovered('voice')}
            onHoverEnd={() => setHovered(null)}
            whileHover={{ y: -4, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="relative text-left rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer group"
            style={{
              borderColor: hovered === 'voice' ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.02)',
              boxShadow: hovered === 'voice' ? '0 0 40px rgba(201,168,76,0.12), 0 24px 60px rgba(0,0,0,0.6)' : '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            <div className="absolute inset-0 opacity-20 transition-opacity duration-300 group-hover:opacity-30"
              style={{
                backgroundImage: 'url(https://images.pexels.com/photos/18572796/pexels-photo-18572796.jpeg?auto=compress&cs=tinysrgb&w=940)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,8,8,0.92) 0%, rgba(8,8,8,0.75) 100%)' }} />

            <div className="relative z-10 p-7">
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}>
                  <Mic size={24} />
                </div>
                <motion.div
                  animate={{ x: hovered === 'voice' ? 0 : -4, opacity: hovered === 'voice' ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight size={20} style={{ color: '#C9A84C' }} />
                </motion.div>
              </div>

              <div className="font-mono-accent text-[10px] text-[#C9A84C] tracking-widest uppercase mb-2">For Any Business</div>
              <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
                I run another type of business.
              </h2>
              <p className="text-sm text-white/60 leading-relaxed mb-5">
                Sub-500ms voice AI for any industry. Inbound sales, outbound campaigns,
                customer support, and reactivation — all from one intelligent agent.
              </p>

              <div className="space-y-2">
                {['<500ms response time, 10+ languages', 'Inbound, outbound, support & reactivation', 'Salesforce, HubSpot, Zendesk & 1000s more'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 size={12} style={{ color: '#C9A84C', flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
              </div>

              <div
                className="mt-6 flex items-center justify-between px-4 py-3 rounded-xl"
                style={{
                  background: hovered === 'voice' ? '#C9A84C' : 'rgba(201,168,76,0.1)',
                  transition: 'background 0.2s',
                }}
              >
                <span className="text-sm font-semibold" style={{ color: hovered === 'voice' ? '#0a0a0a' : '#C9A84C' }}>
                  {selected === '/voice' ? 'Taking you there…' : 'Request Info'}
                </span>
                <ArrowRight size={16} style={{ color: hovered === 'voice' ? '#0a0a0a' : '#C9A84C' }} />
              </div>
            </div>
          </motion.button>
        </motion.div>

        {/* George proof strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <div className="flex -space-x-2">
            <img src="https://ugc.production.linktr.ee/c1557bd3-0db3-4f04-a35b-b6332f19a89e_IMG-0412.jpeg"
              alt="George" className="w-9 h-9 rounded-full border-2 object-cover" style={{ borderColor: '#0a0a0a' }} />
            {[1,2,3].map(i => (
              <div key={i} className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-xs font-bold"
                style={{ borderColor: '#0a0a0a', background: `hsl(${i*60+20},25%,18%)`, color: '#C9A84C' }}>
                {['GM','CEO','VP'][i-1]}
              </div>
            ))}
          </div>
          <p className="text-sm text-white/50 text-center sm:text-left">
            Backed by <span className="text-white/80 font-medium">@georgejsaliba</span> · Exclusive early access for forward-thinking businesses
          </p>
          <div className="flex">
            {[1,2,3,4,5].map(i => <Star key={i} size={12} fill="#C9A84C" style={{ color: '#C9A84C' }} />)}
          </div>
        </motion.div>
      </main>

      {/* Bottom divider */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.2), transparent)' }} />

      <footer className="relative z-10 flex items-center justify-center py-4">
        <p className="text-xs text-white/25">© {new Date().getFullYear()} SalibaLabs.ai · All rights reserved</p>
      </footer>
    </div>
  );
}
